/**
 * Pure conditional-field-visibility resolution for FormShell sections.
 *
 * Kept in a React-free `.ts` module so it can be unit-tested under the
 * `node --test` sweep (which strips TypeScript types but does not transform
 * JSX). FormShell re-exports `resolveVisibleSections` from here, so the
 * component and the tests exercise exactly the same logic.
 */

import type * as React from 'react';

/**
 * A declarative form section. Sections render in order, each with an optional
 * heading + description, and — critically — an optional `visible` predicate
 * that resolves conditional-field visibility against the current form state.
 *
 * This lets modules express dependencies the archetype previously forced them
 * to hand-roll outside the shell, e.g. "show Equipment only when
 * OperatingMode === 'Auto'" or "show Rework Destination only when Disposition
 * === 'Rework'". When `visible(state)` returns false the entire section is
 * skipped (not rendered, not in the DOM) so its inputs don't participate in
 * tab order or validation.
 */
export interface FormSection<TState = Record<string, unknown>> {
  /** Stable id (React key + section landmark). */
  id: string;
  /** Optional section heading. */
  title?: React.ReactNode;
  /** Optional section description. */
  description?: React.ReactNode;
  /** Section body — the fields. May be static or computed from form state. */
  content: React.ReactNode | ((state: TState) => React.ReactNode);
  /**
   * Conditional visibility predicate, evaluated against the current
   * `formState`. When it returns `false` the section is skipped entirely.
   * Omit to always render the section.
   */
  visible?: (state: TState) => boolean;
}

/**
 * Resolve which sections are visible for the given form state. A section with
 * no `visible` predicate always shows; otherwise it is included only when the
 * predicate returns true. Pure (no React) so it's unit-testable and can be
 * reused by callers that need to mirror the shell's visibility decision
 * (e.g. to decide which fields participate in validation).
 */
export function resolveVisibleSections<TState>(
  sections: FormSection<TState>[] | undefined,
  state: TState,
): FormSection<TState>[] {
  return (sections ?? []).filter((s) => (s.visible ? s.visible(state) : true));
}
